/**
 * The `filterType` that each filter component's model is expected to carry.
 *
 * The keys are the filter components configured per column type in `ColTypeColDefMap`, so that map
 * stays the single source of truth for which filter a column gets.
 */
const FilterComponentFilterTypeMap: Record<string, string> = {
  agTextColumnFilter: 'text',
  agNumberColumnFilter: 'number',
  dateFilter: 'date',
  repositoryFilter: 'select',
};

/**
 * Checks whether a filter model may be sent for a column.
 *
 * The backend selects its filter expression class solely from the `filterType` of a column filter
 * and then reads the column value accordingly - a `date` or `number` expression on a text column
 * raises `EInvalidCast` and the whole request fails. Because the ag-grid filter model is keyed by
 * the column position and the backend reports `field` as the column index, a filter can end up on a
 * different attribute than the one it was set on whenever a table changes its column set.
 *
 * Unknown filter components and models without a `filterType` are accepted: only a known mismatch
 * is rejected, so custom filters keep working.
 *
 * @param filter - The filter component configured on the column definition.
 * @param expression - The ag-grid filter model of that column.
 * @returns True if the filter model matches the column's filter, false if it is known to mismatch.
 */
export function isFilterExpressionCompatible(filter: unknown, expression: unknown): boolean {
  // A column without a filter can never carry a valid filter model.
  if (filter == null || filter === false) return false;

  const filterType: unknown = (expression as { filterType?: unknown })?.filterType;
  if (typeof filterType !== 'string') return true;

  if (typeof filter !== 'string') return true;

  const expectedFilterType: string = FilterComponentFilterTypeMap[filter];
  if (!expectedFilterType) return true;

  return expectedFilterType === filterType;
}
